/**
 * Pure presentation primitives for platform-requirement checklist mismatches.
 *
 * These helpers turn evaluation-time evidence (allowed forms/categories and the
 * classifications identified in each evaluated attachment) into short,
 * human-scannable labels. They are intentionally free of any I/O, entity, or
 * NestJS dependency so the exact same formatting can be reused by the
 * document-service SkySlope auto-return comment and by the client agent-review
 * and resubmission surfaces.
 *
 * Rules encoded here (see BLO-13599 design):
 * - Render forms as `Friendly name (CODE)` when a friendly name is known,
 *   otherwise the raw form code.
 * - Allowed forms and allowed categories are alternatives, joined with `or`.
 * - Prefer forms over categories for an attachment classification: when a
 *   classification carries a form, its category is suppressed.
 * - Cap each attachment at a small number of identities followed by `+N more`;
 *   the checklist requirement itself is never capped.
 */

const DEFAULT_MAX_IDENTITIES_PER_ATTACHMENT = 2;

/**
 * A single allowed/identified form: the platform form code plus an optional
 * evaluation-time friendly catalog name.
 */
export interface PlatformRequirementFormReference {
  code: string;
  name?: string | null;
}

/**
 * One contract-document classification identified inside an evaluated
 * attachment. A classification may carry a form, a document category, both, or
 * neither.
 */
export interface PlatformRequirementClassificationReference {
  form?: PlatformRequirementFormReference | null;
  documentCategory?: string | null;
}

/**
 * One evaluated attachment (an attached PDF) and the classifications Indigo
 * identified within it during the recorded evaluation.
 */
export interface PlatformRequirementAttachmentReference {
  documentName: string;
  classifications: PlatformRequirementClassificationReference[];
}

/**
 * The structured, surface-agnostic presentation of a single checklist-activity
 * mismatch. Consumers add their own outer prose, magic link, and signature.
 */
export interface ChecklistMismatchPresentation {
  /** e.g. `Weichert Compensation Worksheet (T501), Broker ... or any ...`. */
  checklistRequirement: string;
  /** One line per evaluated attachment, e.g. `• combined.pdf — Foo (T208)`. */
  attachmentLines: string[];
}

const NO_IDENTITY_LABEL = 'No form or category identified';
const NOT_RECORDED_LABEL = 'Not recorded';

/**
 * Formats a form reference as `Friendly name (CODE)` when a non-empty friendly
 * name is present, otherwise the raw, trimmed form code.
 */
export function formatPlatformRequirementForm(
  form: PlatformRequirementFormReference
): string {
  const code = form.code.trim();
  const name = form.name?.trim();
  return name ? `${name} (${code})` : code;
}

/**
 * Humanizes a document-category enum value into sentence case, e.g.
 * `BROKER_COMPENSATION` -> `Broker compensation`.
 */
export function formatPlatformRequirementCategory(category: string): string {
  return category
    .trim()
    .split('_')
    .filter((word) => word.length > 0)
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase()
    )
    .join(' ');
}

/**
 * Joins alternative labels with `or` semantics:
 * - `A`
 * - `A or B`
 * - `A, B, or C`
 */
function joinAlternatives(parts: string[]): string {
  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0];
  }
  if (parts.length === 2) {
    return `${parts[0]} or ${parts[1]}`;
  }
  const head = parts.slice(0, -1).join(', ');
  return `${head}, or ${parts[parts.length - 1]}`;
}

/**
 * Builds the checklist-requirement string: allowed forms first (each as a
 * friendly label), then allowed categories humanized as `any {category}
 * document`, joined as alternatives. Forms are deduplicated by normalized code,
 * categories by normalized enum value, preserving first appearance.
 */
function buildChecklistRequirement(
  allowedForms: PlatformRequirementFormReference[],
  allowedDocumentCategories: string[]
): string {
  const seenFormCodes = new Set<string>();
  const formLabels: string[] = [];
  for (const form of allowedForms) {
    const normalizedCode = form.code.trim().toUpperCase();
    if (normalizedCode.length === 0 || seenFormCodes.has(normalizedCode)) {
      continue;
    }
    seenFormCodes.add(normalizedCode);
    formLabels.push(formatPlatformRequirementForm(form));
  }

  const seenCategories = new Set<string>();
  const categoryLabels: string[] = [];
  for (const category of allowedDocumentCategories) {
    const normalized = category.trim().toUpperCase();
    if (normalized.length === 0 || seenCategories.has(normalized)) {
      continue;
    }
    seenCategories.add(normalized);
    categoryLabels.push(
      `any ${formatPlatformRequirementCategory(category)} document`
    );
  }

  return (
    joinAlternatives([...formLabels, ...categoryLabels]) || NOT_RECORDED_LABEL
  );
}

type AttachmentIdentity =
  | { kind: 'form'; normalizedKey: string; label: string }
  | { kind: 'category'; normalizedKey: string; label: string };

/**
 * Derives the ordered, deduplicated, form-preferring identities for one
 * attachment. Forms are stably partitioned before category-only identities.
 */
function buildAttachmentIdentities(
  classifications: PlatformRequirementClassificationReference[]
): AttachmentIdentity[] {
  const formIdentities: AttachmentIdentity[] = [];
  const categoryIdentities: AttachmentIdentity[] = [];
  const seenFormCodes = new Set<string>();
  const seenCategories = new Set<string>();

  for (const classification of classifications) {
    const formCode = classification.form?.code?.trim();
    if (formCode) {
      // Prefer the form: its category (if any) is suppressed.
      const normalizedKey = formCode.toUpperCase();
      if (!seenFormCodes.has(normalizedKey)) {
        seenFormCodes.add(normalizedKey);
        formIdentities.push({
          kind: 'form',
          normalizedKey,
          label: formatPlatformRequirementForm(classification.form!),
        });
      }
      continue;
    }

    const category = classification.documentCategory?.trim();
    if (category) {
      const normalizedKey = category.toUpperCase();
      if (!seenCategories.has(normalizedKey)) {
        seenCategories.add(normalizedKey);
        categoryIdentities.push({
          kind: 'category',
          normalizedKey,
          label: `Category: ${formatPlatformRequirementCategory(category)}`,
        });
      }
    }
  }

  return [...formIdentities, ...categoryIdentities];
}

/**
 * Renders one attachment line: `• {name} — {identity, identity, +N more}`.
 */
function buildAttachmentLine(
  attachment: PlatformRequirementAttachmentReference,
  maxIdentities: number
): string {
  const identities = buildAttachmentIdentities(attachment.classifications);
  const name = attachment.documentName.trim() || NOT_RECORDED_LABEL;

  if (identities.length === 0) {
    return `• ${name} — ${NO_IDENTITY_LABEL}`;
  }

  const shown = identities.slice(0, maxIdentities).map((i) => i.label);
  const overflow = identities.length - shown.length;
  const parts = overflow > 0 ? [...shown, `+${overflow} more`] : shown;
  return `• ${name} — ${parts.join(', ')}`;
}

/**
 * Builds the structured checklist-mismatch presentation shared by the SkySlope
 * auto-return comment and the agent-review/resubmission surfaces.
 */
export function buildChecklistMismatchPresentation(input: {
  allowedForms: PlatformRequirementFormReference[];
  allowedDocumentCategories: string[];
  attachments: PlatformRequirementAttachmentReference[];
  maxIdentitiesPerAttachment?: number;
}): ChecklistMismatchPresentation {
  const maxIdentities =
    input.maxIdentitiesPerAttachment ?? DEFAULT_MAX_IDENTITIES_PER_ATTACHMENT;

  return {
    checklistRequirement: buildChecklistRequirement(
      input.allowedForms,
      input.allowedDocumentCategories
    ),
    attachmentLines: input.attachments.map((attachment) =>
      buildAttachmentLine(attachment, maxIdentities)
    ),
  };
}
